import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarHeart, Flame, Heart, MessageCircle, MoonStar, RefreshCw, Sparkles, User, Users } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const PINK = "#D97B8F";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function MonCompte() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/my/account").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!data) {
    return <div className="flex min-h-[60vh] items-center justify-center text-sm text-stone2" data-testid="account-loading">Chargement de votre compte…</div>;
  }

  const accessCard = (() => {
    if (!data.paid) {
      return {
        icon: Sparkles,
        title: "Votre test vous attend",
        text: "Débloquez le test de compatibilité pour révéler votre façon d'aimer et rencontrer vos profils compatibles pendant 1 mois.",
        cta: { to: "/quiz", label: "Débloquer mon test" },
      };
    }
    if (!data.quiz_done) {
      return {
        icon: Sparkles,
        title: "Votre tirage vous attend",
        text: "Votre test est débloqué — dix questions, quatre cartes par question, et votre façon d'aimer se révèle.",
        cta: { to: "/quiz", label: "Faire mon test" },
      };
    }
    if (data.match_access_active) {
      const days = data.match_access_days_left;
      return {
        icon: CalendarHeart,
        title: "Votre accès rencontres est actif",
        text: days != null
          ? `Il vous reste ${days} jour${days > 1 ? "s" : ""} de matchs et de messagerie — jusqu'au ${formatDate(data.match_access_expires_at)}.`
          : "Vos matchs et votre messagerie sont ouverts.",
        progress: days != null ? Math.min(100, Math.round((days / 31) * 100)) : null,
      };
    }
    return {
      icon: MoonStar,
      title: "Votre mois de rencontres est terminé",
      text: "Pour recevoir de nouveaux matchs, prolongez votre accès d'un mois. Vous pouvez aussi refaire le test si vous le souhaitez — aucune obligation, vos réponses sont enregistrées.",
      cta: { to: "/quiz", label: "Prolonger mon accès" },
    };
  })();
  const AccessIcon = accessCard.icon;

  const tiles = [
    { to: "/matches", icon: Users, title: "Mes matchs", sub: `${data.matches_count} match${data.matches_count > 1 ? "s" : ""}`, badge: data.unread_messages, testid: "account-tile-matches" },
    { to: "/matches", icon: MessageCircle, title: "Ma messagerie", sub: data.unread_messages > 0 ? `${data.unread_messages} message${data.unread_messages > 1 ? "s" : ""} non lu${data.unread_messages > 1 ? "s" : ""}` : "Vos conversations", testid: "account-tile-chat" },
    { to: "/profile", icon: Sparkles, title: "Mon tirage", sub: data.archetype_label || "Pas encore de tirage", testid: "account-tile-quiz" },
    { to: "/quiz", icon: RefreshCw, title: "Refaire le test", sub: "Un nouveau mois de rencontres", testid: "account-tile-retake" },
    { to: "/profile", icon: User, title: "Mon profil", sub: "Photo, ville, rayon de recherche", testid: "account-tile-profile" },
    { to: "/discover", icon: Heart, title: "Découvrir", sub: "Vos profils compatibles", testid: "account-tile-discover" },
  ];

  return (
    <div className="relative min-h-[calc(100vh-64px)] overflow-hidden bg-gradient-to-b from-[#FBEAE8] via-cream to-[#F9E3E1]" data-testid="account-page">
      <img src="/assets/mandala-sun.jpeg" alt="" className="pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full object-cover opacity-[0.14]" />
      <img loading="lazy" src="/assets/mandala-arrow.jpeg" alt="" className="pointer-events-none fixed inset-0 h-full w-full object-cover opacity-[0.05]" />

      <div className="relative mx-auto max-w-4xl px-4 py-12 sm:px-8">
        <div className="text-center">
          <p className="font-cinzel text-xs tracking-[0.35em] text-gold">MON COMPTE</p>
          <h1 className="mt-3 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl" data-testid="account-title">Bonjour, {data.name}</h1>
          <p className="mx-auto mt-3 max-w-md font-serif text-base italic text-clay">
            Votre petit univers APINITY : vos cartes, vos matchs et vos soirées, réunis ici.
          </p>
          <div className="mx-auto mt-5 h-px w-24 bg-gradient-to-r from-transparent via-[#D97B8F] to-transparent" />
        </div>

        {/* Accès rencontres */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-10 rounded-3xl border border-[#D97B8F]/30 bg-white/90 p-7 text-center shadow-card backdrop-blur-sm sm:p-9"
          data-testid="account-access-card"
        >
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#D97B8F]/15">
            <AccessIcon size={28} className="text-[#C4637A]" />
          </div>
          <h2 className="mt-4 font-serif text-2xl font-medium text-ink" data-testid="account-access-title">{accessCard.title}</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-clay" data-testid="account-access-text">{accessCard.text}</p>
          {accessCard.progress != null && (
            <div className="mx-auto mt-4 h-2 max-w-xs overflow-hidden rounded-full bg-[#D97B8F]/15" data-testid="account-access-progress">
              <div className="h-full rounded-full bg-gradient-to-r from-[#D97B8F] to-[#B53A55] transition-all duration-700" style={{ width: `${accessCard.progress}%` }} />
            </div>
          )}
          {accessCard.cta && (
            <Link to={accessCard.cta.to} data-testid="account-access-cta" className="group mt-5 inline-flex items-center gap-2 rounded-full bg-terra px-7 py-3 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:scale-[1.03] hover:bg-terra-dark">
              {accessCard.cta.label}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          )}
        </motion.div>

        {/* Raccourcis */}
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="account-tiles">
          {tiles.map((t, i) => (
            <motion.div key={t.title} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 + i * 0.06, duration: 0.5 }}>
              <Link
                to={t.to}
                data-testid={t.testid}
                className="group relative flex h-full flex-col items-center gap-2 rounded-2xl border border-[#D97B8F]/25 bg-white/90 p-5 text-center shadow-boho backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#D97B8F]/70 hover:shadow-card"
              >
                {t.badge > 0 && (
                  <span className="absolute right-3 top-3 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#D97B8F] px-1.5 text-[10px] font-bold text-white" data-testid={`${t.testid}-badge`}>
                    {t.badge}
                  </span>
                )}
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#D97B8F]/12 transition-colors duration-300 group-hover:bg-[#D97B8F]/25">
                  <t.icon size={19} style={{ color: PINK }} />
                </span>
                <p className="font-serif text-base font-semibold text-ink">{t.title}</p>
                <p className="text-[11px] leading-snug text-stone2">{t.sub}</p>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Réservations Speed Dating */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.6 }}
          className="mt-8 rounded-3xl border border-[#5C1E28]/15 bg-white/90 p-7 shadow-boho backdrop-blur-sm sm:p-8"
          data-testid="account-reservations"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#5C1E28]/10">
              <Flame size={19} className="text-[#5C1E28]" />
            </span>
            <div>
              <h2 className="font-serif text-xl font-semibold text-ink">Mes soirées Speed Dating</h2>
              <p className="text-xs text-stone2">Saint-Germain-en-Laye · 20h00 · une consommation offerte</p>
            </div>
          </div>
          {data.reservations.length === 0 ? (
            <div className="mt-5 text-center">
              <p className="text-sm text-clay">Aucune réservation pour le moment — la bougie n'attend que vous.</p>
              <Link to="/speed-dating" data-testid="account-speed-link" className="mt-4 inline-block rounded-full bg-[#5C1E28] px-6 py-2.5 text-xs font-semibold text-cream transition-all duration-300 hover:bg-[#3E0F18]">
                Découvrir les soirées
              </Link>
            </div>
          ) : (
            <ul className="mt-5 space-y-3">
              {data.reservations.map((r, i) => (
                <li key={i} className="flex items-center justify-between rounded-2xl border border-[#5C1E28]/10 bg-[#FDF3EF] px-5 py-3.5" data-testid={`account-reservation-${i}`}>
                  <div>
                    <p className="font-serif text-base font-semibold text-ink">Speed Dating {r.age_group_label}</p>
                    <p className="text-[11px] text-stone2">Réservée le {formatDate(r.created_at)}{r.paid_via === "promo" ? " · invitation" : ""}</p>
                  </div>
                  <span className="rounded-full bg-[#5C1E28]/10 px-3 py-1 text-[11px] font-semibold text-[#5C1E28]">Confirmée</span>
                </li>
              ))}
            </ul>
          )}
        </motion.div>
      </div>
    </div>
  );
}
